import Highcharts from "highcharts";
import { useEffect } from "react";

import { useTranslation } from "@/lib/i18n";
import type Timezone from "@/lib/timezone/Timezone";

/**
 * Apply global Highcharts defaults — localized weekday names and the dashboard
 * timezone — for every chart rendered while this hook is mounted.
 *
 * `Highcharts.setOptions` is a *global* defaults setter, so call this once high
 * in a page/route (e.g. the dashboard page or the admin layout), never per
 * widget or per preview. It runs as an effect so it never mutates global state
 * during React's render phase, and no-ops until a timezone is resolved.
 */
export function useHighchartsTimezoneDefaults(timezone: Timezone | undefined): void {
  const { t } = useTranslation();

  useEffect(() => {
    if (!timezone) return;
    // https://github.com/highcharts/highcharts/issues/21900 — negative offsets
    // with minutes are miscalculated, so prefer the named zone unless it is a
    // whole-hour UTC offset.
    const hasOffset = timezone.name === "UTC" && timezone.offset !== "+00:00";
    Highcharts.setOptions({
      lang: {
        shortWeekdays: [
          t("chart.short_weekdays.sun"),
          t("chart.short_weekdays.mon"),
          t("chart.short_weekdays.tue"),
          t("chart.short_weekdays.wed"),
          t("chart.short_weekdays.thu"),
          t("chart.short_weekdays.fri"),
          t("chart.short_weekdays.sat"),
        ],
        weekdays: [
          t("chart.weekdays.sun"),
          t("chart.weekdays.mon"),
          t("chart.weekdays.tue"),
          t("chart.weekdays.wed"),
          t("chart.weekdays.thu"),
          t("chart.weekdays.fri"),
          t("chart.weekdays.sat"),
        ],
      },
      time: {
        timezone: hasOffset ? timezone.offset : timezone.name,
      },
      credits: {
        enabled: false,
      },
    });
  }, [timezone, t]);
}
